import { Languages } from '#lib/enum.js';
import registry from '../../tutorials/registry.json' with { type: 'json' };

/** @typedef {{ id: string, title: string, section: string, audience: string, youtubeId: string, sourceHash: string, srtHash: string }} GuideRegistryVideo */

const SECTION_ORDER = ['getting-started'];

/**
 * @returns {GuideRegistryVideo[]}
 */
export function listRegistryVideos() {
  const videos = registry?.videos && typeof registry.videos === 'object' ? registry.videos : {};
  return Object.values(videos);
}

/**
 * Registry rows that have a YouTube id (the marketing catalog).
 * @returns {GuideRegistryVideo[]}
 */
export function listPublishedGuides() {
  return listRegistryVideos().filter((video) => Boolean(video?.youtubeId));
}

/**
 * @param {string} id
 * @returns {GuideRegistryVideo | null}
 */
export function getPublishedGuide(id) {
  const video = registry?.videos?.[id];
  if (!video?.youtubeId) {
    return null;
  }
  return video;
}

/**
 * Locale-neutral detail paths for prerender and sitemap.
 * @returns {string[]}
 */
export function publishedGuidePagePaths() {
  return listPublishedGuides().map((video) => `/guides/${video.id}`);
}

/**
 * Published guides grouped by section. Section order is stable.
 * @returns {Array<{ section: string, videos: GuideRegistryVideo[] }>}
 */
export function publishedGuidesBySection() {
  /** @type {Map<string, GuideRegistryVideo[]>} */
  const groups = new Map();
  for (const video of listPublishedGuides()) {
    const list = groups.get(video.section) ?? [];
    list.push(video);
    groups.set(video.section, list);
  }

  const orderedSections = [
    ...SECTION_ORDER.filter((section) => groups.has(section)),
    ...[...groups.keys()].filter((section) => !SECTION_ORDER.includes(section)).sort(),
  ];

  return orderedSections.map((section) => ({
    section,
    videos: groups.get(section) ?? [],
  }));
}

/**
 * Player UI (`hl`) and caption preference (`cc_lang_pref`) for a marketing locale.
 * Caption pref follows the site language so a later fr/zh SRT is picked automatically.
 * @param {string} [locale]
 * @returns {{ hl: string, ccLangPref: string }}
 */
function youtubeEmbedLanguage(locale) {
  if (locale === Languages.French) {
    return { hl: 'fr', ccLangPref: 'fr' };
  }
  if (locale === Languages.Chinese) {
    return { hl: 'zh-CN', ccLangPref: 'zh' };
  }
  return { hl: 'en', ccLangPref: 'en' };
}

/**
 * Privacy-enhanced embed URL. YouTube is the file host, not the public catalog.
 * @param {string} youtubeId
 * @param {string} [locale] - Marketing locale (`en`, `fr`, `zh`)
 * @returns {string}
 */
export function youtubeNocookieEmbedUrl(youtubeId, locale) {
  const { hl, ccLangPref } = youtubeEmbedLanguage(locale);
  const params = new URLSearchParams({
    rel: '0',
    cc_load_policy: '1',
    hl,
    cc_lang_pref: ccLangPref,
  });
  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`;
}
